import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface ItemMetadataProps {
  createdAt: string;
  createdBy?: string | null;
  updatedAt: string;
  updatedBy?: string | null;
  /** When true, renders as a small icon with tooltip instead of inline text */
  compact?: boolean;
}

/**
 * Displays "Créé le XXX par XXX | Mis à jour le XXX par XXX" in a discreet grey style.
 * Fetches admin profile names from user_admin_profile.
 * In compact mode, shows a small info icon with metadata in a tooltip.
 */
export function ItemMetadata({ createdAt, createdBy, updatedAt, updatedBy, compact }: ItemMetadataProps) {
  const userIds = [createdBy, updatedBy].filter(Boolean) as string[];

  const { data: profiles } = useQuery({
    queryKey: ["admin-profiles-meta", ...userIds],
    queryFn: async () => {
      if (userIds.length === 0) return {};
      const { data } = await supabase
        .from("user_admin_profile")
        .select("user_id, first_name, last_name")
        .in("user_id", userIds);
      const map: Record<string, string> = {};
      for (const p of data || []) {
        const name = [p.first_name, p.last_name].filter(Boolean).join(" ");
        map[p.user_id] = name || "Inconnu";
      }
      return map;
    },
    enabled: userIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const getName = (id?: string | null) => {
    if (!id) return null;
    return profiles?.[id] || null;
  };

  const createdName = getName(createdBy);
  const updatedName = getName(updatedBy);

  const createdPart = `Créé le ${fmt(createdAt)}${createdName ? ` (${createdName})` : ""}`;

  // Only show updated part if updated_at differs from created_at by more than 1 second
  const showUpdated = Math.abs(new Date(updatedAt).getTime() - new Date(createdAt).getTime()) > 1000;
  const updatedPart = showUpdated ? `MAJ le ${fmt(updatedAt)}${updatedName ? ` (${updatedName})` : ""}` : null;

  if (compact) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help shrink-0" />
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">{createdPart}</p>
            {updatedPart && <p className="text-xs">{updatedPart}</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <p className="text-xs text-muted-foreground/70 mt-1">
      {createdPart}
      {updatedPart && <> · {updatedPart}</>}
    </p>
  );
}
